import { useEffect, useRef, useState, useCallback } from "react";
import "./App.css";
import AIEngine from "./aiEngine";
import AIRouter from "./ai-router";

/* ==========================================================================
   smartcalcAi — React UI wiring the enhanced AIEngine.
   Preserves original data-testids and structure (all element IDs match the
   original vanilla implementation for backward compatibility).
   ========================================================================== */

function escapeHtml(str) {
  if (str === null || str === undefined) return "";
  return String(str).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

const STARTER_SUGGESTIONS = [
  "15% of 2500",
  "emi for 5 lakh at 8.5% for 5 years",
  "sip of 5000 per month at 12% for 10 years",
  "bmi 70kg 170cm",
  "100 usd to inr",
  "cagr from 10000 to 25000 in 5 years",
  "lcm of 12 and 18",
  "years between 5 Jan 2005 and today",
];

export default function App() {
  const viewportRef = useRef(null);
  const inputRef = useRef(null);
  const [bubbles, setBubbles] = useState([
    {
      side: "ai",
      kind: "chat",
      message:
        "Hi! I'm **smartcalcAi** — your intelligent AI calculator. Ask me anything in English, Hindi or Hinglish. Try one of the pills below to get started.",
      suggestions: STARTER_SUGGESTIONS.slice(0, 4),
    },
  ]);
  const [isMuted, setIsMuted] = useState(false);
  const [isDark, setIsDark] = useState(
    localStorage.getItem("globalDarkMode") === "enabled"
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ai_smart_history")) || [];
    } catch {
      return [];
    }
  });
  const [toast, setToast] = useState("");
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  // NEW: autocomplete suggestions shown below the input as the user types.
  // Purely additive — input remains uncontrolled (ref-based) exactly as before.
  const [autoSuggestions, setAutoSuggestions] = useState([]);
  const pendingRef = useRef({ intentId: null, context: "" });
  const recognizerRef = useRef(null);

  /* -------- theme -------- */
  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDark);
    localStorage.setItem("globalDarkMode", isDark ? "enabled" : "disabled");
  }, [isDark]);

  /* -------- scroll on new bubble -------- */
  useEffect(() => {
    const v = viewportRef.current;
    if (v) v.scrollTop = v.scrollHeight;
  }, [bubbles, thinking]);

  /* -------- toast auto-dismiss -------- */
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  /* -------- history persist -------- */
  useEffect(() => {
    localStorage.setItem("ai_smart_history", JSON.stringify(history));
  }, [history]);

  const speak = useCallback(
    (text) => {
      if (isMuted || !text || !("speechSynthesis" in window)) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text.replace(/[*_`#]/g, ""));
      window.speechSynthesis.speak(u);
    },
    [isMuted]
  );

  const push = (b) => setBubbles((prev) => [...prev, b]);

  const submitQuery = useCallback(
    (rawText) => {
      const text = (rawText ?? inputRef.current?.value ?? "").trim();
      if (!text) return;
      push({ side: "user", kind: "text", text });
      if (inputRef.current) inputRef.current.value = "";
      setThinking(true);

      setTimeout(() => {
        let outcome;
        let usedText = text;
        try {
          if (pendingRef.current.intentId) {
            usedText = (pendingRef.current.context + " " + text).trim();
            outcome = AIRouter.retryWithContext(
              pendingRef.current.intentId,
              usedText
            );
            if (outcome.response.mode !== "result") {
              const fresh = AIRouter.classify(text);
              // fresh.response may now be a Promise (the router escalates
              // unresolved queries to the Knowledge Base / Project Data / AI
              // provider chain asynchronously) — treat that as worth
              // adopting too, not just a synchronous 'result'/'chat', or a
              // good async AI answer would be silently dropped in favor of
              // the stale unresolved clarify prompt.
              if (
                fresh.isAsync ||
                fresh.response.mode === "result" ||
                fresh.response.mode === "chat"
              ) {
                outcome = fresh;
                usedText = text;
              }
            }
          } else {
            outcome = AIRouter.classify(text);
          }
        } catch {
          outcome = { intentId: null, response: { mode: "unknown", suggestions: [] } };
        }

        const finalize = (resolved) => {
          setThinking(false);
          const { intentId, response } = { intentId: outcome.intentId, response: resolved };
          if (response.mode === "result") {
            pendingRef.current = { intentId: null, context: "" };
            // NEW: attach the classifier's confidence score (if present) to the
            // bubble data so it can be shown as a small badge on the result.
            push({ side: "ai", kind: "result", data: { ...response, confidence: outcome.confidence } });
            setHistory((h) => [
              { id: Date.now(), query: text, res: response.result },
              ...h,
            ].slice(0, 200));
            speak(response.explain);
          } else if (response.mode === "clarify") {
            pendingRef.current = { intentId, context: usedText };
            push({ side: "ai", kind: "clarify", data: response });
            speak(
              response.missing?.length
                ? "I need a bit more information. " + response.missing.join(", ")
                : response.hint || ""
            );
          } else if (response.mode === "chat") {
            pendingRef.current = { intentId: null, context: "" };
            push({
              side: "ai",
              kind: "chat",
              message: response.message,
              suggestions: response.suggestions || [],
              learningLinks: response.learningLinks, // NEW: passthrough for learning-topic chat replies
            });
            speak(response.message);
          } else {
            pendingRef.current = { intentId: null, context: "" };
            push({
              side: "ai",
              kind: "unknown",
              message: response.message, // NEW: rotating fallback phrasing (falls back to default text if absent)
              didYouMean: response.didYouMean, // NEW: closest-match suggestions from the engine
              suggestions: response.suggestions || STARTER_SUGGESTIONS.slice(0, 4),
              learningLinks: response.learningLinks, // NEW: passthrough for learning-topic unknown replies
            });
          }
        };

        if (outcome.isAsync) {
          Promise.resolve(outcome.response)
            .then(finalize)
            .catch(() => finalize({ mode: "unknown", suggestions: [] }));
        } else {
          finalize(outcome.response);
        }
      }, 220);
    },
    [speak]
  );

  const handleSuggestionClick = (q) => {
    pendingRef.current = { intentId: null, context: "" };
    submitQuery(q);
  };

  /* -------- speech recognition -------- */
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = "en-IN";
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      if (inputRef.current) inputRef.current.value = t;
      submitQuery(t);
    };
    rec.onerror = rec.onend = () => setListening(false);
    recognizerRef.current = rec;
  }, [submitQuery]);

  const toggleMic = () => {
    const rec = recognizerRef.current;
    if (!rec) {
      setToast("Speech recognition not supported in this browser");
      return;
    }
    if (listening) rec.stop();
    else {
      try {
        rec.start();
        setListening(true);
      } catch {
        setListening(false);
      }
    }
  };

  /* -------- copy -------- */
  const copyResult = (val) => {
    navigator.clipboard.writeText(String(val));
    setToast("Copied to clipboard!");
  };

  return (
    <div className="iq-shell" data-testid="iq-app-shell">
      {/* Top bar */}
      <header className="iq-topbar">
        <div className="iq-brand" data-testid="iq-brand">
          <span className="iq-brand-mark">SC</span>
          <div className="iq-brand-text">
            <div className="iq-brand-title">smartcalcAi</div>
            <div className="iq-brand-sub">AI Calculator</div>
          </div>
        </div>
        <div className="iq-actions">
          <button
            className="iq-icon-btn"
            id="history-toggle"
            data-testid="btn-history-open"
            onClick={() => setHistoryOpen(true)}
            aria-label="History"
          >
            <i className="fa-solid fa-clock-rotate-left" />
          </button>
          <button
            className="iq-icon-btn"
            id="mute-voice-btn"
            data-testid="btn-mute-voice"
            onClick={() => {
              setIsMuted((m) => {
                const next = !m;
                if (next && "speechSynthesis" in window)
                  window.speechSynthesis.cancel();
                setToast(next ? "Voice muted" : "Voice unmuted");
                return next;
              });
            }}
            aria-label="Toggle voice"
          >
            <i
              id="mute-icon-node"
              className={
                isMuted ? "fa-solid fa-volume-xmark" : "fa-solid fa-volume-high"
              }
            />
          </button>
          <button
            className="iq-icon-btn"
            id="theme-toggle"
            data-testid="btn-theme-toggle"
            onClick={() => setIsDark((d) => !d)}
            aria-label="Toggle theme"
          >
            <i className={isDark ? "fa-solid fa-sun" : "fa-solid fa-moon"} />
          </button>
        </div>
      </header>

      {/* Chat viewport */}
      <main
        className="iq-chat-viewport"
        id="chat-stream-viewport"
        data-testid="chat-viewport"
        ref={viewportRef}
      >
        {bubbles.map((b, idx) => (
          <Bubble
            key={idx}
            bubble={b}
            onSuggest={handleSuggestionClick}
            onCopy={copyResult}
          />
        ))}
        {thinking && (
          <div
            className="chat-bubble bubble-ai bubble-thinking"
            data-testid="bubble-thinking"
          >
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
            <span className="thinking-label">Computing…</span>
          </div>
        )}
      </main>

      {/* Suggestions track */}
      <div
        className="suggestions-track-row"
        id="suggestions-track-row"
        data-testid="suggestions-track"
      >
        {STARTER_SUGGESTIONS.map((s) => (
          <span
            key={s}
            className="suggest-pill"
            data-testid={`starter-pill-${s.slice(0, 12).replace(/\s+/g, "-")}`}
            data-q={s}
            onClick={() => handleSuggestionClick(s)}
          >
            {s}
          </span>
        ))}
      </div>

      {/* Input row */}
      {/* NEW: wrapper div only for positioning the autocomplete dropdown — the
          original .iq-input-row below is completely unchanged. */}
      <div className="iq-input-wrap">
      <div className="iq-input-row" data-testid="input-row">
        <button
          id="speech-recognition-mic"
          className={`iq-mic-btn ${listening ? "listening-active" : ""}`}
          data-testid="btn-mic"
          onClick={toggleMic}
          aria-label="Voice input"
        >
          <i
            className={
              listening
                ? "fa-solid fa-microphone-lines"
                : "fa-solid fa-microphone"
            }
          />
        </button>
        <input
          ref={inputRef}
          id="terminal-query-input"
          data-testid="input-query"
          className="iq-input"
          placeholder="Ask anything… e.g. EMI for 5 lakh at 8% for 5 years"
          onChange={(e) => {
            // NEW: autocomplete — suggest known example queries as the user types.
            const val = e.target.value;
            if (val.trim().length >= 2) {
              setAutoSuggestions(AIEngine.autocomplete(val, 5));
            } else {
              setAutoSuggestions([]);
            }
          }}
          onBlur={() => {
            // small delay so a click on a dropdown item registers before it unmounts
            setTimeout(() => setAutoSuggestions([]), 150);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setAutoSuggestions([]);
              submitQuery();
            }
            if (e.key === "Escape") setAutoSuggestions([]);
          }}
        />
        <button
          id="query-submit-trigger"
          data-testid="btn-submit"
          className="iq-submit-btn"
          onClick={() => {
            setAutoSuggestions([]);
            submitQuery();
          }}
          aria-label="Submit"
        >
          <i className="fa-solid fa-paper-plane" />
        </button>
      </div>
      {/* NEW: autocomplete dropdown — only rendered when there are suggestions */}
      {autoSuggestions.length > 0 && (
        <div className="iq-autocomplete-dropdown" data-testid="autocomplete-dropdown">
          {autoSuggestions.map((s, i) => (
            <div
              key={i}
              className="autocomplete-item"
              data-testid={`autocomplete-item-${i}`}
              onMouseDown={(e) => {
                // onMouseDown (fires before input's onBlur) so the click registers
                e.preventDefault();
                setAutoSuggestions([]);
                handleSuggestionClick(s);
              }}
            >
              <i className="fa-solid fa-magnifying-glass" />
              <span>{s}</span>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* History panel */}
      <aside
        id="history-panel"
        className={`iq-history-panel ${historyOpen ? "open" : ""}`}
        data-testid="history-panel"
      >
        <div className="iq-history-head">
          <div>History</div>
          <div className="iq-history-head-actions">
            <button
              id="clear-history"
              data-testid="btn-clear-history"
              className="iq-link-btn"
              onClick={() => setHistory([])}
            >
              Clear
            </button>
            <button
              id="history-close"
              data-testid="btn-history-close"
              className="iq-icon-btn"
              onClick={() => setHistoryOpen(false)}
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>
        <div id="history-list" className="iq-history-list">
          {history.length === 0 ? (
            <div className="empty-history-msg" data-testid="history-empty">
              No computations logged yet.
            </div>
          ) : (
            history.map((item) => (
              <div
                key={item.id}
                className="history-item"
                data-testid={`history-item-${item.id}`}
                onClick={() => {
                  setHistoryOpen(false);
                  submitQuery(item.query);
                }}
              >
                <span className="hist-query">{item.query}</span>
                <span className="hist-res">{String(item.res)}</span>
              </div>
            ))
          )}
        </div>
      </aside>
      {historyOpen && (
        <div
          className="iq-scrim"
          data-testid="history-scrim"
          onClick={() => setHistoryOpen(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div id="toast" className="iq-toast show" data-testid="toast">
          {toast}
        </div>
      )}
    </div>
  );
}

// ===== NEW FEATURE START =====
// Reusable list of external link cards, used for both "Related Calculators"
// and "Learning Resources" sections below. All links open in a new tab.
function LinkList({ title, icon, items, testidPrefix }) {
  if (!items || items.length === 0) return null;
  return (
    <>
      <div className="ai-meta-segment">{title}</div>
      <div className="ai-link-list" data-testid={testidPrefix}>
        {items.map((item, i) => (
          <a
            key={i}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="ai-link-card"
            data-testid={`${testidPrefix}-${i}`}
          >
            <i className={`fa-solid ${icon}`} />
            <span>{item.title}</span>
            <i className="fa-solid fa-arrow-up-right-from-square ai-link-card-go" />
          </a>
        ))}
      </div>
    </>
  );
}
// ===== NEW FEATURE END =====

/* ==========================================================================
   Bubble component — renders each message
   ========================================================================== */
function Bubble({ bubble, onSuggest, onCopy }) {
  if (bubble.side === "user") {
    return (
      <div
        className="chat-bubble bubble-user"
        data-testid="bubble-user"
      >
        {bubble.text}
      </div>
    );
  }
  if (bubble.kind === "result") {
    const d = bubble.data;
    return (
      <div className="chat-bubble bubble-ai" data-testid="bubble-result">
        <div className="ai-meta-segment">{d.type}</div>
        {/* NEW: confidence badge — only shown when the classifier provided a score */}
        {typeof d.confidence === "number" && d.confidence > 0 && (
          <div className="ai-confidence-row" data-testid="confidence-badge">
            <span className="ai-confidence-badge">
              {Math.round(d.confidence * 100)}% confidence
            </span>
          </div>
        )}
        <div className="ai-result-header">{String(d.result)}</div>
        <div className="ai-meta-segment">Formula</div>
        <div className="ai-formula">{d.formula}</div>
        <div className="ai-meta-segment">Step Computation</div>
        <div className="ai-math-box">{d.calc}</div>
        <div className="ai-explain">{d.explain}</div>
        {d.related && d.related.length > 0 && (
          <>
            <div className="ai-meta-segment">Related</div>
            <div className="ai-related">
              {d.related.map((r, i) => (
                <span key={i} className="related-chip">
                  {r}
                </span>
              ))}
            </div>
          </>
        )}
        {d.warnings && d.warnings.length > 0 && (
          <div className="ai-warning">⚠️ {d.warnings.join(" ")}</div>
        )}
        {/* ===== NEW FEATURE START ===== */}
        <LinkList
          title="Related Calculators"
          icon="fa-calculator"
          items={d.relatedCalculators}
          testidPrefix="related-calculator"
        />
        <LinkList
          title="Learning Resources"
          icon="fa-graduation-cap"
          items={d.learningLinks}
          testidPrefix="learning-link"
        />
        {/* ===== NEW FEATURE END ===== */}
        {d.suggestions && d.suggestions.length > 0 && (
          <div className="suggestions-row bubble-suggestions">
            {d.suggestions.map((s, i) => (
              <span
                key={i}
                className="suggest-pill bubble-suggest-pill"
                data-testid={`suggest-pill-${i}`}
                onClick={() => onSuggest(s)}
              >
                {s}
              </span>
            ))}
          </div>
        )}
        <div className="bubble-actions">
          <button
            className="action-link btn-copy-bubble-res"
            data-testid="btn-copy-result"
            onClick={() => onCopy(d.result)}
          >
            <i className="fa-regular fa-copy" /> Copy
          </button>
        </div>
      </div>
    );
  }
  if (bubble.kind === "clarify") {
    const d = bubble.data;
    const hasMissing = d.missing && d.missing.length;
    return (
      <div className="chat-bubble bubble-ai" data-testid="bubble-clarify">
        <div className="clarify-title">
          {hasMissing
            ? "I need a little more detail to calculate this. Please provide:"
            : d.hint ||
              "I couldn't complete that calculation — please try rephrasing."}
        </div>
        {hasMissing && (
          <ul className="clarify-list">
            {d.missing.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        )}
        {hasMissing && d.hint && <div className="clarify-hint">{d.hint}</div>}
      </div>
    );
  }
  if (bubble.kind === "chat") {
    return (
      <div className="chat-bubble bubble-ai" data-testid="bubble-chat">
        <div
          className="chat-text"
          dangerouslySetInnerHTML={{
            __html: renderChatMarkup(bubble.message),
          }}
        />
        {/* ===== NEW FEATURE START ===== */}
        <LinkList
          title="Learning Resources"
          icon="fa-graduation-cap"
          items={bubble.learningLinks}
          testidPrefix="chat-learning-link"
        />
        {/* ===== NEW FEATURE END ===== */}
        {bubble.suggestions && bubble.suggestions.length > 0 && (
          <div className="suggestions-row bubble-suggestions">
            {bubble.suggestions.map((s, i) => (
              <span
                key={i}
                className="suggest-pill bubble-suggest-pill"
                data-testid={`chat-suggest-pill-${i}`}
                onClick={() => onSuggest(s)}
              >
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }
  // unknown
  return (
    <div className="chat-bubble bubble-ai" data-testid="bubble-unknown">
      {/* ===== NEW FEATURE START ===== (rotating message; falls back to the
          original fixed text if the engine didn't provide one) */}
      <div>{bubble.message || "I couldn't quite work that one out. Try rephrasing, or tap an example:"}</div>
      {bubble.didYouMean && bubble.didYouMean.length > 0 && (
        <div className="did-you-mean-row" data-testid="did-you-mean">
          <span className="did-you-mean-label">Did you mean:</span>
          {bubble.didYouMean.map((s, i) => (
            <span
              key={i}
              className="suggest-pill bubble-suggest-pill"
              data-testid={`did-you-mean-pill-${i}`}
              onClick={() => onSuggest(s)}
            >
              {s}
            </span>
          ))}
        </div>
      )}
      {/* ===== NEW FEATURE END ===== */}
      <LinkList
        title="Learning Resources"
        icon="fa-graduation-cap"
        items={bubble.learningLinks}
        testidPrefix="unknown-learning-link"
      />
      {bubble.suggestions && bubble.suggestions.length > 0 && (
        <div className="suggestions-row bubble-suggestions">
          {bubble.suggestions.map((s, i) => (
            <span
              key={i}
              className="suggest-pill bubble-suggest-pill"
              data-testid={`unknown-suggest-pill-${i}`}
              onClick={() => onSuggest(s)}
            >
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function renderChatMarkup(str) {
  let safe = escapeHtml(str);
  safe = safe.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  safe = safe.replace(/\n/g, "<br/>");
  return safe;
}